/**
 * One-time migration off the monolith's flat localStorage keys.
 *
 * aragela-live-api-demo.html stored the session as five separate string
 * entries (aragela_api_key, aragela_role, …) and the language as a sixth.
 * Zustand's persist middleware keeps one JSON blob per store instead, so
 * without this a browser already signed into the demo would land on the
 * login gate after the cutover — a small thing that reads as "the new
 * version logged everyone out".
 *
 * Runs before the stores are created (imported at the top of each store),
 * writes the blob persist expects, and clears the legacy keys so it is
 * genuinely one-time. Safe to keep indefinitely; a no-op once the old keys
 * are gone.
 */
const LEGACY = {
  apiKey: 'aragela_api_key',
  role: 'aragela_role',
  driverId: 'aragela_driver_id',
  displayName: 'aragela_display_name',
  tenantId: 'aragela_tenant_id',
  lang: 'aragela_lang',
};

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // private-mode / storage-disabled browsers
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ state: value, version: 0 }));
  } catch {
    /* nothing sensible to do — the app works, it just will not remember */
  }
}

function drop(keys) {
  try {
    keys.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export function migrateLegacySession() {
  if (typeof window === 'undefined') return;

  // Targets the console scope specifically: the monolith had no separate
  // driver bundle, so a carried-over session belongs to the console.
  const apiKey = read(LEGACY.apiKey);
  if (apiKey && !read('aragela_auth_console')) {
    write('aragela_auth_console', {
      apiKey,
      role: read(LEGACY.role),
      driverId: read(LEGACY.driverId) || null,
      tenantId: read(LEGACY.tenantId) || null,
      displayName: read(LEGACY.displayName) || '',
    });
  }

  const lang = read(LEGACY.lang);
  if (lang && !read('aragela_ui')) {
    write('aragela_ui', { view: 'dispatcher', lang });
  }

  drop([LEGACY.apiKey, LEGACY.role, LEGACY.driverId, LEGACY.displayName, LEGACY.tenantId, LEGACY.lang]);
}
